import React from "react";
import { UploadSidebar } from "./UploadSidebar";
import { FaDownload } from "../../utils/icons";
import type { UploadListItem } from "./types";
import type { BatchCase, BatchPhase } from "../../hooks/useBatchUpload";

/**
 * Worklist-shaped layout shell for the Upload page (LU-A10).
 *
 * Renders the same two-pane chrome as the worklist (`grid-cols-[320px_1fr]`,
 * `border-r`, `bg-bg`): the left `UploadSidebar` (Upload button + uploads list)
 * and a right pane with a header band (title+subtitle; cleared for completed studies) + content.
 *
 * It is a pure layout — the page (logic lane) decides which view goes in
 * `children` based on its phase / selection (see index.ts for the mapping) and
 * owns all state + handlers. Mounts inside the app's `<main>` (below the global
 * Navbar), so no navbar here.
 */
interface UploadPageViewProps {
  // --- left sidebar ---
  uploads: UploadListItem[];
  selectedId?: string | null;
  onSelectUpload: (id: string) => void;
  onNewUpload: () => void;
  /** Remove an upload from the list (e.g. dismiss a failed one). */
  onDeleteUpload?: (id: string) => void;
  /** True while composing a brand-new upload (no past upload selected). */
  newUploadActive?: boolean;
  batch?: { phase: BatchPhase; cases: BatchCase[] };
  batchSelected?: boolean;
  onSelectBatch?: () => void;
  /**
   * Export the worklist as CSV. Renders as a button at the right of the header
   * band, only when there is at least one upload. The file is always a full
   * snapshot of the worklist — the sidebar's status filter does not narrow it,
   * which is why the label says "all".
   */
  onExport?: () => void;
  /** Export a report for every completed upload as one HTML document. */
  onExportReports?: () => void;
  exportingReports?: boolean;

  /** Right-pane header. Omit `title` to clear the band (a completed study). */
  title?: string;
  subtitle?: React.ReactNode;

  /**
   * When true the content area is a non-scrolling flex column (so a child that
   * manages its own scroll — e.g. the detail PreviewGalleryView — works). When
   * false (default) the content area scrolls. Set true for the detail view.
   */
  contentFills?: boolean;

  /** The right-pane body — one of the upload view panels. */
  children: React.ReactNode;
}

export const UploadPageView: React.FC<UploadPageViewProps> = ({
  uploads,
  selectedId,
  onSelectUpload,
  onNewUpload,
  onDeleteUpload,
  newUploadActive = false,
  batch,
  batchSelected,
  onSelectBatch,
  onExport,
  onExportReports,
  exportingReports = false,
  title,
  subtitle,
  contentFills = false,
  children,
}) => (
  <div
    className="grid h-full min-h-0 grid-cols-[320px_1fr] bg-bg text-fg"
    data-testid="upload-study-page"
  >
    <UploadSidebar
      uploads={uploads}
      selectedId={selectedId}
      onSelect={onSelectUpload}
      onNewUpload={onNewUpload}
      onDelete={onDeleteUpload}
      newUploadActive={newUploadActive}
      batch={batch}
      batchSelected={batchSelected}
      onSelectBatch={onSelectBatch}
    />

    <section className="flex min-h-0 flex-col bg-bg">
      {/* Top band — aligns with the Upload-study button. Shows title + subtitle
          for the new-upload flow and processing/failed studies; cleared (no
          title) for a completed, successful study. */}
      <header className="flex h-[60px] flex-none items-center justify-between gap-4 border-b border-border bg-bg-2 px-5">
        {title && (
          <div className="min-w-0">
            <h2 className="truncate text-[17px] font-semibold tracking-tight">{title}</h2>
            {subtitle && (
              <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.08em] text-fg-dim">
                {subtitle}
              </div>
            )}
          </div>
        )}
        {/* `ml-auto` pins the export right even when the band has no title (a
            completed study), where justify-between alone would left-align it. */}
        {(onExport || onExportReports) && uploads.length > 0 && (
          <div className="ml-auto flex shrink-0 items-center gap-2">
            {onExportReports && (
              <button
                onClick={onExportReports}
                disabled={exportingReports}
                data-testid="upload-export-reports-button"
                className="flex items-center gap-1 rounded-xs border border-border-strong px-2 py-1 font-mono text-[9px] text-fg-muted transition-colors hover:text-fg disabled:cursor-wait disabled:opacity-60"
                title="Export reports for all completed uploads as PDF"
              >
                <FaDownload className="h-2.5 w-2.5" />
                {exportingReports ? "Preparing reports…" : "Export reports"}
              </button>
            )}
            {onExport && (
              <button
                onClick={onExport}
                data-testid="upload-export-button"
                className="flex items-center gap-1 rounded-xs border border-border-strong px-2 py-1 font-mono text-[9px] text-fg-muted transition-colors hover:text-fg"
                title="Export all uploads as CSV"
              >
                <FaDownload className="h-2.5 w-2.5" />
                Export CSV
              </button>
            )}
          </div>
        )}
      </header>
      <div
        className={
          contentFills
            ? "flex min-h-0 flex-1 flex-col px-5 py-4"
            : "min-h-0 flex-1 overflow-y-auto px-5 py-4 custom-scrollbar"
        }
      >
        {children}
      </div>
    </section>
  </div>
);
